import classNames from 'classnames';
import { Data, Payment, UI, Utils } from 'common';
import dompurify from 'dompurify';
import isNull from 'lodash/isNull';
import isObject from 'lodash/isObject';
import remove from 'lodash/remove';
import uniqBy from 'lodash/uniqBy';
import React, { Component } from 'react';
import AccountCommunication from '../utils/AccountCommunication';
import { ActionType } from './ActionType.enum';
import { CardType } from './CardType.enum';
import { PaymentAccountType } from './PaymentAccountType.enum';

const { States } = Data;
const { Util: PaymentUtil } = Payment;

const {
	Logger,
	Validation,
	withLayoutAndModalContexts,
	Util: {
		generateMonthArray,
		generateYearArray,
		formatZip,
		differences,
		convertObjectToArray,
	},
	Session,
} = Utils;

const {
	WrappedInputTextWithLabel,
	WrappedDropdownWithLabel,
	WrappedButton,
	WrappedButtonType,
	WrappedTooltip,
} = UI;

const generateNameState = inputName => {
	switch (inputName) {
		case 'NickName':
			return 'nickName';
		case 'AccountNumberDisplayDigits':
			return 'cardNumber';
		case 'AccountHolderName':
			return 'cardHolder';
		case 'addressLine1':
			return 'address1';
		case 'addressLine2':
			return 'address2';
		default:
			return inputName;
	}
};

const generateAddress = paymentInfo =>
	paymentInfo
		? `${paymentInfo.Address1},
	${paymentInfo.City},
	${paymentInfo.State},
	${paymentInfo.Zip}`
		: '';

const hasWhiteSpaces = value => /\s/g.test(value.trim());

const formatCardOnChange = (number = '', accountType) => {
	const val = number.replace(/\D/g, '');

	if (accountType === CardType.Amex) {
		return [val.substring(0, 4), val.substring(4, 10), val.substring(10, 15)]
			.join(' ')
			.trim();
	} else {
		// visa, master, discover
		return [
			val.substring(0, 4),
			val.substring(4, 8),
			val.substring(8, 12),
			val.substring(12, 16),
			val.substring(16, 19),
		]
			.join(' ')
			.trim();
	}
};

/**
 * Return credit card type based on the credit card number.
 * (source: https://baymard.com/checkout-usability/credit-card-patterns)
 * @param {string} number
 * @return {string} 'Visa', 'Master', 'Discover', or 'AMEX'
 */
const getCreditCardTypeByNumber = number => {
	// American Express begins with 34, 37
	const amexCardReg = /^3[47]/;

	// Master Card begins with 51‑55, or 222100‑272099 (2221 through 2720)
	const masterCardReg = /^(5[1-5]|222[1-9]|22[3-9]|2[3-6]|27[01]|2720)/;

	// Visa card begins with 4
	const visaCardReg = /^4/;

	// Discover card begins with 6011, 622126‑622925, 644‑649, or 65
	const discoverCardReg = /^6([045]|22)/;

	if (amexCardReg.test(number)) {
		return CardType.Amex;
	} else if (masterCardReg.test(number)) {
		return CardType.Master;
	} else if (visaCardReg.test(number)) {
		return CardType.Visa;
	} else if (discoverCardReg.test(number)) {
		return CardType.Discover;
	} else {
		return null;
	}
};

// For Master and American Express, we are sending the right accountType in a payload to save payment method
const processAccountType = accountType => {
	if (accountType === CardType.Master) {
		return CardType.MasterCard;
	} else if (accountType === CardType.Amex) {
		return CardType.AmericanExpress;
	}

	return accountType;
};

class ManagePayment extends Component {
	constructor(props) {
		super(props);

		this.state = {
			paymentAccountId: '',
			accountType: '',
			accountNumber: '',
			nickName: '',
			cardNumber: '',
			cardHolder: '',
			billingAddressSelection: 0,
			billingAddressOptions: [],
			currentBillingAddresses: [],
			address1: '',
			address2: '',
			city: '',
			state: '',
			zipCode: '',
			month: 0,
			year: 0,
			cvv: '',
			validations: {
				cardNumberMessage: '',
				cardHolderMessage: '',
				cvvMessage: '',
				cardHolder: 0,
				cardNumber: 0,
				address1: 0,
				city: 0,
				state: 0,
				zipCode: 0,
				cvv: 0,
			},
			clean: true,
			yearArr: [],
			monthArr: [],
			isUpdating: false,
		};
	}

	componentDidMount() {
		const {
			data: { paymentInfo, allAccounts = [], action },
		} = this.props;

		const { billingAddressOptions, currentBillingAddresses } =
			this.shapeCurrentBillingAddresses(allAccounts);
		const yearOptions = generateYearArray(10);
		const monthOptions = generateMonthArray();

		if (action === ActionType.EDIT) {
			const dateTime = paymentInfo.ExpiryDate
				? paymentInfo.ExpiryDate.split('/')
				: [monthOptions[0].value, yearOptions[0].value];
			const currentAddressIndex = currentBillingAddresses.findIndex(
				item => item.Address1 === paymentInfo.BillingAddress.Address1
			);

			this.setState({
				paymentAccountId: paymentInfo.PaymentAccountId,
				accountType: paymentInfo.AccountType,
				accountNumber: paymentInfo.AccountNumber,
				nickName: paymentInfo.NickName,
				cardHolder: paymentInfo.AccountHolderName,
				cardNumber: `**** **** **** ${paymentInfo.AccountNumberDisplayDigits}`,
				billingAddressSelection: currentAddressIndex,
				billingAddressOptions,
				currentBillingAddresses,
				month: dateTime[0],
				year: dateTime[1],
				yearArr: yearOptions,
				monthArr: monthOptions,
			});
		} else if (action === ActionType.CREATE) {
			this.setState({
				month: monthOptions[0].value,
				year: yearOptions[0].value,
				yearArr: yearOptions,
				monthArr: monthOptions,
				billingAddressSelection: 0,
				billingAddressOptions,
				currentBillingAddresses,
			});
		}
	}

	validateData = () => {
		const {
			cardHolder,
			cardNumber,
			accountType,
			address1,
			city,
			state,
			zipCode,
			validations,
			billingAddressSelection,
			cvv,
		} = this.state;

		const newValidation = { ...validations };

		if (cardNumber.length === 0) {
			newValidation.cardNumber = -1;
			newValidation.cardNumberMessage = 'Card Number is required';
		} else if (cardNumber.search(/(\*+\s)+[0-9]+/g) !== -1) {
			// Check if the existing card number was changed.
			newValidation.cardNumber = -1;
			newValidation.cardNumberMessage = 'Please enter a new card number';
		} else {
			newValidation.cardNumber = 1;
		}

		if (cardHolder.length === 0) {
			newValidation.cardHolder = -1;
			newValidation.cardHolderMessage = 'Cardholder name is required.';
		} else if (!hasWhiteSpaces(cardHolder)) {
			// Check if cardHolder field has separated first & last name.
			newValidation.cardHolder = -1;
			newValidation.cardHolderMessage =
				'Please enter your first and last name separated by a space';
		} else {
			newValidation.cardHolder = 1;
		}

		if (cvv.length === 0) {
			newValidation.cvv = -1;
			newValidation.cvvMessage = 'CVV is required';
		} else if (
			(accountType === CardType.Amex && cvv.length !== 4) ||
			(accountType !== CardType.Amex && cvv.length !== 3)
		) {
			newValidation.cvv = -1;
			newValidation.cvvMessage = 'Invalid CVV';
		} else {
			newValidation.cvv = 1;
		}

		if (billingAddressSelection === 0) {
			if (address1.length === 0) {
				newValidation.address1 = -1;
			} else {
				newValidation.address1 = 1;
			}
			if (city.length === 0) {
				newValidation.city = -1;
			} else {
				newValidation.city = 1;
			}
			if (state.length === 0) {
				newValidation.state = -1;
			} else {
				newValidation.state = 1;
			}
			if (zipCode.length === 0) {
				newValidation.zipCode = -1;
			} else {
				newValidation.zipCode = 1;
			}
		} else {
			// Resets validation when an existing address is selected.
			newValidation.address1 = 0;
			newValidation.city = 0;
			newValidation.state = 0;
			newValidation.zipCode = 0;
		}

		this.setState({
			validations: newValidation,
		});

		const validationData = Object.values(newValidation);
		const foundError = validationData.filter(item => item === -1);

		return foundError.length === 0;
	};

	onChange = e => {
		let { value, name } = e.target;
		const { accountType } = this.state;

		const inputName = generateNameState(name);

		if (inputName === 'zipCode') {
			value = formatZip(value);
		}
		// Reset when editing a payment
		if (inputName === 'cardNumber') {
			if (value.search(/(\*+\s)+[0-9]+/g) !== -1) {
				value = '';
				this.checkCard(); // Reset card type on delete
			} else {
				this.checkCard(value);
				value = formatCardOnChange(value, accountType);
			}
		}

		this.setState({
			[inputName]: value,
			clean: false,
		});
	};

	checkCard = number => {
		const accountType = getCreditCardTypeByNumber(number);

		this.setState({ accountType });
	};

	shapeCurrentBillingAddresses = allAccounts => {
		// Get session account address
		const { customerDetails = {} } = Session.getAccountInfo();
		const accountAddress = customerDetails.address || {};

		// Reshape Accounts array
		const formattedAddresses = allAccounts.map(paymentAccount => {
			if (paymentAccount.PaymentAccountType !== PaymentAccountType.ACH) {
				return {
					Address1: paymentAccount.BillingAddress.Address1,
					Address2: paymentAccount.BillingAddress.Address2,
					City: paymentAccount.BillingAddress.City,
					State: paymentAccount.BillingAddress.State,
					Zip: paymentAccount.BillingAddress.Zip,
				};
			}
			return null;
		});

		// Concatenate
		let currentBillingAddresses = [
			...formattedAddresses,
			{
				Address1: accountAddress.addressLine1,
				Address2: accountAddress.addressLine2,
				City: accountAddress.city,
				State: accountAddress.stateProvince,
				Zip: accountAddress.postalCode,
			},
		];

		// Remove null object
		remove(currentBillingAddresses, isNull);

		// Remove duplicates
		currentBillingAddresses = uniqBy(currentBillingAddresses, 'Address1');

		// Options array for dropdown.
		const billingAddressOptions = currentBillingAddresses.map(
			(account, index) => {
				const address = generateAddress(account);
				return {
					label: address,
					value: index + 1,
				};
			}
		);

		// Add default value for options array
		billingAddressOptions.unshift({
			label: 'New Billing Address',
			value: 0,
		});

		// Add empty values so index isn't mismatched with billing index
		currentBillingAddresses.unshift({
			Address1: '',
			Address2: '',
			City: '',
			State: '',
			Zip: '',
		});

		// Return dropdown options array & currentAddresses
		return { billingAddressOptions, currentBillingAddresses };
	};

	showCustomError = () => {
		const {
			modalContext: { showError },
			data,
		} = this.props;
		const label = data && data.action === ActionType.EDIT ? 'Edit' : 'Add';

		showError({
			data: {
				handleType: 'custom',
				title: `${label} Payment Method Error`,
				description:
					'We could not process your request. Please verify that your payment information is correct and try again.',
			},
		});
	};

	addPaymentMethod = async () => {
		const {
			data: { callback, isCorpAccount, customerId },
			modalId,
			modalContext: { closeModal },
		} = this.props;

		const { nickName, cardHolder, cardNumber, accountType, month, year, cvv } =
			this.state;

		const isValid = this.validateData();

		if (!isValid) {
			return;
		}

		const body = {
			CustId: customerId,
			PaymentAccount: {
				PaymentAccountType: PaymentAccountType.CreditCard,
				AccountType: processAccountType(accountType),
				NickName: nickName,
				AccountHolderName: cardHolder.replace(/\s+/g, ' '),
				ExpiryDate: `${month}/${year}`,
				AccountNumber: cardNumber.replace(/\s/g, ''),
				BillingAddress: this.buildBillingAddress(),
				CVN: cvv,
			},
		};

		this.setState({
			isUpdating: true,
		});

		let response;
		if (isCorpAccount) {
			response =
				await AccountCommunication.createCorporationPaymentAccount(body);
		} else {
			response = await AccountCommunication.createPaymentAccount(body);
		}

		this.setState({
			isUpdating: false,
		});

		if (AccountCommunication.isSuccessful(response)) {
			callback({ status: true });
			this.setState({ clean: true });
			closeModal(modalId);
		} else {
			this.showCustomError();
			this.setState({ clean: false });
			callback({
				status: false,
				errorDescription: response.ErrorDescription,
				errorCode: response.ErrorCode,
			});
		}

		Logger.saveActionData('Save Payment Account Button Clicked');
	};

	editPaymentMethod = async () => {
		const {
			data: {
				callback,
				isCorpAccount,
				customerId,
				paymentAccountForChargeCategory,
			},
			modalId,
			modalContext: { showError, closeModal },
		} = this.props;

		const {
			nickName,
			cardHolder,
			cardNumber,
			month,
			year,
			accountType,
			paymentAccountId,
			accountNumber,
			cvv,
		} = this.state;

		const isValid = this.validateData();

		if (!isValid) {
			return;
		}

		const body = {
			CustId: customerId,
			PaymentAccount: {
				PaymentAccountId: paymentAccountId,
				PaymentAccountType: PaymentAccountType.CreditCard,
				AccountType: processAccountType(accountType),
				NickName: nickName,
				AccountHolderName: cardHolder.replace(/\s+/g, ' '),
				ExpiryDate: `${month}/${year}`,
				AccountNumber: cardNumber.replace(/\s/g, '') || accountNumber,
				BillingAddress: this.buildBillingAddress(),
				CVN: cvv,
			},
		};

		// check if the paymentAccountId is being used for "Service Fees"
		const isUpdatingServiceFeesPaymentAccount =
			isObject(paymentAccountForChargeCategory) &&
			paymentAccountForChargeCategory.ServiceFee === paymentAccountId;

		this.setState({
			isUpdating: true,
		});

		const response = isCorpAccount
			? await AccountCommunication.updateCorporationPaymentAccount(body)
			: await AccountCommunication.updatePaymentAccount(body);

		this.setState({
			isUpdating: false,
		});

		if (AccountCommunication.isSuccessful(response)) {
			this.setState({ clean: true });

			// When we update the credit card tied to the service fees,
			// call swsim ResubmitPurchase endpoint if user's account is in resubmit state,
			if (
				isUpdatingServiceFeesPaymentAccount === true &&
				PaymentUtil.isInResubmitState()
			) {
				const resubmitPurchaseResponse = await PaymentUtil.resubmitPurchase();

				if (!AccountCommunication.isSuccessful(resubmitPurchaseResponse)) {
					showError({
						response: resubmitPurchaseResponse,
					});
				}
			}

			callback({ status: true });
			closeModal(modalId);
		} else {
			this.showCustomError();
			this.setState({ clean: false });
			callback({
				status: false,
				errorDescription: response.ErrorDescription,
				errorCode: response.ErrorCode,
			});
		}

		Logger.saveActionData('Save Payment Account Button Clicked');
	};

	buildBillingAddress = () => {
		const {
			billingAddressSelection,
			currentBillingAddresses,
			address1,
			address2,
			city,
			state,
			zipCode,
		} = this.state;
		let Address = {};

		if (billingAddressSelection === 0) {
			Address = {
				Address1: address1,
				Address2: address2,
				City: city,
				State: state,
				Zip: zipCode.split('-')[0],
			};
		} else {
			const { Address1, Address2, City, State, Zip } =
				currentBillingAddresses[billingAddressSelection];

			Address = {
				Address1,
				Address2,
				City,
				State,
				Zip: Zip.split('-')[0],
			};
		}

		return Address;
	};

	updateNickName = async () => {
		const { paymentAccountId, nickName } = this.state;
		const {
			data: { callback, isCorpAccount, customerId },
			modalId,
			modalContext: { showError, closeModal },
		} = this.props;

		this.setState({
			isUpdating: true,
		});

		let response;

		if (isCorpAccount) {
			response = await AccountCommunication.updateCorporationPaymentNickname(
				paymentAccountId,
				nickName
			);
		} else {
			response = await AccountCommunication.updatePaymentNickname(
				paymentAccountId,
				nickName,
				customerId
			);
		}

		this.setState({
			isUpdating: false,
		});

		if (AccountCommunication.isSuccessful(response)) {
			callback({ status: true });
			this.setState({ clean: true });
			closeModal(modalId);
		} else {
			this.setState({ clean: false });
			showError({ response });
			callback({
				status: false,
				errorDescription: response.ErrorDescription,
				errorCode: response.ErrorCode,
			});
		}
	};

	updatePaymentMethodOnSave = () => {
		const {
			data: { action, paymentInfo },
		} = this.props;
		const {
			nickName,
			cardHolder,
			cardNumber,
			month,
			year,
			accountType,
			paymentAccountId,
			accountNumber,
		} = this.state;

		// per WEBAPPS-25697, improve the existing logging
		Logger.logToServer({
			category: 'save_payment_method',
			method: action === ActionType.EDIT ? 'edit' : 'add',
		});

		if (action === ActionType.EDIT) {
			const updatedData = {
				AccountHolderName: cardHolder.replace(/\s+/g, ' '),
				AccountNumber: cardNumber.includes('*')
					? accountNumber
					: cardNumber.replace(/\s/g, ''),
				AccountNumberDisplayDigits: cardNumber.slice(cardNumber.length - 4),
				AccountType: accountType,
				BillingAddress: this.buildBillingAddress(),
				ExpiryDate: `${month}/${year}`,
				NickName: nickName,
				PaymentAccountId: paymentAccountId,
				PaymentAccountType: PaymentAccountType.CreditCard,
			};
			const changedFields = convertObjectToArray(
				differences(updatedData, paymentInfo)
			);

			if (changedFields.length === 1 && changedFields[0] === 'NickName') {
				this.updateNickName();
			} else {
				this.editPaymentMethod();
			}
		} else {
			this.addPaymentMethod();
		}
	};

	renderCreditCardIcon = () => {
		const { accountType } = this.state;

		if (accountType === CardType.Visa) {
			return <span className="visa-card" />;
		} else if (accountType === CardType.Master) {
			return <span className="master-card" />;
		} else if (accountType === CardType.Discover) {
			return <span className="discover-card" />;
		} else if (accountType === CardType.Amex) {
			return <span className="american-express-card" />;
		}

		return null;
	};

	autoCompleteChange = (e, type) => {
		switch (type) {
			case 'month':
				this.setState({
					month: parseInt(e.target.value),
					clean: false,
				});
				break;
			case 'year':
				this.setState({
					year: parseInt(e.target.value),
					clean: false,
				});
				break;
			case 'state':
				this.setState({
					state: e.target.value,
					clean: false,
				});
				break;
			default:
				break;
		}
	};

	render() {
		const {
			nickName,
			cardNumber,
			cardHolder,
			billingAddressSelection,
			billingAddressOptions,
			address1,
			address2,
			city,
			state,
			zipCode,
			month,
			year,
			validations,
			yearArr,
			monthArr,
			clean,
			isUpdating,
			cvv,
			accountType,
		} = this.state;

		const {
			layoutContext: { isMobile },
		} = this.props;

		const mobileClassName = isMobile ? 'mobile-wrapper' : '';
		const cardNumberCls = classNames('card-number', {
			'mobile-wrapper': isMobile === true,
			invalid: validations.cardNumber === -1,
		});

		return (
			<div className={`account-payment manage-payment ${mobileClassName}`}>
				<h3 className="heading">Credit Card Information</h3>
				<div className={`main-add-payment ${mobileClassName}`}>
					<WrappedInputTextWithLabel
						className={`inputtext ${mobileClassName}`}
						label="Nickname:"
						uiElementConfig={{
							type: 'text',
							name: 'NickName',
							maxLength: 42,
							value: nickName,
							onChange: this.onChange,
							dataqa: 'nickname',
						}}
					/>
					<div className={cardNumberCls}>
						<WrappedInputTextWithLabel
							className={`inputtext ${mobileClassName}`}
							isInputInvalid={validations.cardNumber === -1}
							showErrorBelow={isMobile}
							label="Card Number:"
							errorMessage={validations.cardNumberMessage}
							uiElementConfig={{
								type: 'text',
								name: 'AccountNumberDisplayDigits',
								maxLength: accountType === CardType.Visa ? 23 : 19,
								required: true,
								value: cardNumber,
								onChange: this.onChange,
								onKeyPress: Validation.acceptOnlyNumbersInput,
								dataqa: 'card-number',
								autoComplete: 'cc-number',
							}}
						/>
						<div className="credit-cards">{this.renderCreditCardIcon()}</div>
					</div>
					<div className={`expiration ${mobileClassName}`}>
						<WrappedDropdownWithLabel
							className={`month dropdown ${mobileClassName}`}
							label="Expiration Date:"
							uiElementConfig={{
								name: 'month',
								options: monthArr,
								value: parseInt(month),
								onChange: this.onChange,
								dataqa: 'expiration-date-month',
								autoCompleteName: 'cc-exp-month',
								autoCompleteOnChange: e => {
									this.autoCompleteChange(e, 'month');
								},
							}}
						/>
						<WrappedDropdownWithLabel
							className={`year dropdown ${mobileClassName}`}
							uiElementConfig={{
								name: 'year',
								options: yearArr,
								value: parseInt(year),
								onChange: this.onChange,
								dataqa: 'expiration-date-year',
								autoCompleteName: 'cc-exp-year',
								autoCompleteOnChange: e => {
									this.autoCompleteChange(e, 'year');
								},
							}}
						/>
						<div className="cvv">
							<WrappedInputTextWithLabel
								className={`inputtext ${mobileClassName}`}
								label="CVV:"
								uiElementConfig={{
									type: 'text',
									name: 'cvv',
									maxLength: 4,
									required: true,
									value: cvv,
									onChange: this.onChange,
									onKeyPress: Validation.acceptOnlyNumbersInput,
									dataqa: 'cvv',
									autoComplete: 'cc-csc',
								}}
								showErrorBelow={isMobile}
								errorMessage={validations.cvvMessage}
								isInputInvalid={validations.cvv === -1}
							/>
							<WrappedButton
								className="cvv-tooltip-btn"
								buttonType={WrappedButtonType.Icon}
								icon="cvv"
								dataqa="manage-payment-cvv-tooltip"
							/>
							<WrappedTooltip
								target="cvv-tooltip-btn"
								position="bottom"
								className="account-payment cvv-tooltip"
							>
								<p>
									<b>Where's the CVV?</b>
									<br />
									The CVV is your credit card's security code. It is the 3 or 4
									digit code located on the back of most cards.
								</p>
								<div className="cvv-images">
									<span className="cvv-image amex" />
									<span className="cvv-image all-cards" />
								</div>
							</WrappedTooltip>
						</div>
					</div>
					<WrappedInputTextWithLabel
						className={`inputtext ${mobileClassName}`}
						label="Cardholder Name:"
						uiElementConfig={{
							type: 'text',
							name: 'AccountHolderName',
							maxLength: 50,
							required: true,
							value: cardHolder,
							onChange: this.onChange,
							dataqa: 'card-holder-name',
							autoComplete: 'cc-name',
						}}
						showErrorBelow={isMobile}
						errorMessage={validations.cardHolderMessage}
						isInputInvalid={validations.cardHolder === -1}
					/>

					<WrappedDropdownWithLabel
						className={`dropdown billing-address ${mobileClassName}`}
						label="Billing Address:"
						uiElementConfig={{
							name: 'billingAddressSelection',
							placeholder: 'Select Billing Address',
							options: billingAddressOptions,
							value: billingAddressSelection,
							onChange: this.onChange,
							dataqa: 'billing-address',
						}}
					/>

					{billingAddressSelection === 0 ? (
						<div className="new-billing-address">
							<WrappedInputTextWithLabel
								className={`inputtext ${mobileClassName}`}
								label="Address Line 1:"
								uiElementConfig={{
									type: 'text',
									name: 'addressLine1',
									maxLength: 50,
									required: true,
									value: address1,
									onChange: this.onChange,
									dataqa: 'address-1',
									autoComplete: 'address-line1',
								}}
								showErrorBelow={isMobile}
								errorMessage="Address is required."
								isInputInvalid={validations.address1 === -1}
							/>
							<WrappedInputTextWithLabel
								className={`inputtext ${mobileClassName}`}
								label="Address Line 2:"
								uiElementConfig={{
									type: 'text',
									name: 'addressLine2',
									maxLength: 50,
									value: address2,
									onChange: this.onChange,
									dataqa: 'address-2',
									autoComplete: 'address-line2',
								}}
							/>
							<WrappedInputTextWithLabel
								className={`inputtext ${mobileClassName}`}
								label="City:"
								uiElementConfig={{
									type: 'text',
									name: 'city',
									maxLength: 28,
									required: true,
									value: city,
									onChange: this.onChange,
									dataqa: 'city',
									autoComplete: 'locality',
								}}
								showErrorBelow={isMobile}
								errorMessage="City is required."
								isInputInvalid={validations.city === -1}
							/>
							<WrappedDropdownWithLabel
								className={`dropdown ${mobileClassName}`}
								label="State:"
								uiElementConfig={{
									name: 'state',
									value: state,
									onChange: this.onChange,
									options: States,
									optionLabel: 'name',
									optionValue: 'abbreviation',
									maxLength: 20,
									dataqa: 'state',
									autoCompleteName: 'region',
									autoCompleteOnChange: e => {
										this.autoCompleteChange(e, 'state');
									},
								}}
								showErrorBelow={isMobile}
								errorMessage="State is required."
								isInputInvalid={validations.state === -1}
							/>
							<WrappedInputTextWithLabel
								className={`zip-code inputtext ${mobileClassName}`}
								label="ZIP Code:"
								uiElementConfig={{
									type: 'text',
									name: 'zipCode',
									maxLength: 5,
									required: true,
									value: zipCode,
									onChange: this.onChange,
									onKeyPress: Validation.acceptOnlyNumbersInput,
									dataqa: 'zip-code',
									autoComplete: 'postal-code',
								}}
								showErrorBelow={isMobile}
								errorMessage="ZIP Code is required."
								isInputInvalid={validations.zipCode === -1}
							/>
						</div>
					) : (
						''
					)}
				</div>
				<div className="manage-payment-footer">
					<WrappedButton
						buttonType={WrappedButtonType.Primary}
						onClick={this.updatePaymentMethodOnSave}
						dataqa="manage-payment"
						disabled={clean || isUpdating}
						label="Save"
					/>
				</div>
			</div>
		);
	}
}

export default withLayoutAndModalContexts(ManagePayment);
